// Mirrors CreateTcrCampaignRequest::SCREENSHOT_MAX_KILOBYTES.
export const UPLOAD_MAX_BYTES = 2 * 1024 * 1024;
export const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp';
const PASS_THROUGH_TYPES = ['image/png', 'image/jpeg'];
const MAX_PASSES = 4;
const JPEG_QUALITY = 0.7;

const loadImage = (file: File): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => {
            URL.revokeObjectURL(url);
            resolve(image);
        };
        image.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('The file could not be read as an image.'));
        };
        image.src = url;
    });

const encode = (image: HTMLImageElement, scale: number, type: string): Promise<Blob> =>
    new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('The image could not be encoded.'))),
            type,
            JPEG_QUALITY
        );
    });

export const prepareImage = async (file: File): Promise<File> => {
    if (PASS_THROUGH_TYPES.includes(file.type) && file.size <= UPLOAD_MAX_BYTES) {
        return file;
    }

    const image = await loadImage(file);
    // A photo stays JPEG and keeps most of its pixels at the cap; anything else becomes PNG, which never blurs text.
    const jpeg = 'image/jpeg' === file.type;
    const type = jpeg ? 'image/jpeg' : 'image/png';
    const name = jpeg ? file.name : `${file.name.replace(/\.[^.]+$/, '')}.png`;
    let scale = 1;

    for (let pass = 0; pass < MAX_PASSES; pass += 1) {
        const blob = await encode(image, scale, type);

        if (blob.size <= UPLOAD_MAX_BYTES) {
            return new File([blob], name, { type });
        }

        // Size tracks pixel count; shrink proportionally to the overshoot, never below half per pass.
        scale *= Math.max(0.5, Math.sqrt(UPLOAD_MAX_BYTES / blob.size));
    }

    throw new Error('The image is too large even after downscaling; crop it first.');
};
